import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Button, Chip, Stack, Fade } from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import api from '../../../../services/api';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:9000/api/';

const ViewProduct = () => {
    const { id } = useParams();
    const history = useHistory();

    const [product, setProduct] = useState(null);
    const [appId, setAppId] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedVariation, setSelectedVariation] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const getImageUrl = (path) => {
        if (!path) return '/sem_produto.jpg';
        return `${BACKEND_SERVER}v1/apps/${appId}/media/${path}`;
    };

    const getAllImages = (p) => {
        return p.variacao?.flatMap((v) => v.imagens || [])?.filter((img) => img.url) || [];
    };

    const loadProduct = async () => {
        const res = await api.get(`v1/apps/${appId}/products/${id}/`);
        const p = res.data.product;

        setProduct(p);

        const allImages = getAllImages(p);

        if (allImages.length > 0) {
            setSelectedImage(getImageUrl(allImages[0].url));
        }

        if (p.variacao?.length) {
            setSelectedVariation(p.variacao[0]);
        }
    };

    const deleteProduct = async (prod_id) => {
        await api.delete(`v1/apps/${appId}/products/${prod_id}/`);
        history.push(`/dashboard/config/products`);
    };

    useEffect(() => {
        if (!selectedVariation) return;

        const img = selectedVariation.imagens?.find((i) => i.url);

        if (img) {
            setSelectedImage(getImageUrl(img.url));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariation]);

    if (!product) {
        return (
            <Box p={4}>
                <Typography>Carregando produto...</Typography>
            </Box>
        );
    }

    const imagesToShow =
        selectedVariation?.imagens?.length > 0 ? selectedVariation.imagens.filter((img) => img.url) : getAllImages(product);

    const valorVenda = selectedVariation?.valor_venda ?? product.variacao?.[0]?.valor_venda;
    const valorCusto = selectedVariation?.valor_custo ?? product.variacao?.[0]?.valor_custo;

    return (
        <Box sx={{ p: 5, minHeight: '100vh' }}>
            <Grid container spacing={5}>
                {/* GALERIA */}
                <Grid item xs={12} md={6}>
                    <Fade in timeout={400}>
                        <Box>
                            <Box
                                sx={{
                                    background: '#fff',
                                    borderRadius: 4,
                                    p: 2,
                                    mb: 2,
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
                                }}
                            >
                                <img
                                    src={selectedImage || '/sem_produto.jpg'}
                                    alt=""
                                    style={{
                                        width: '100%',
                                        height: 400,
                                        objectFit: 'contain',
                                        borderRadius: 12
                                    }}
                                />
                            </Box>

                            <Stack direction="row" spacing={1} overflow="auto">
                                {imagesToShow.map((img) => {
                                    const url = getImageUrl(img.url);

                                    return (
                                        <Box
                                            key={img.id}
                                            onClick={() => setSelectedImage(url)}
                                            sx={{
                                                border: selectedImage === url ? '2px solid #1976d2' : '2px solid transparent',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                p: 0.5
                                            }}
                                        >
                                            <img
                                                src={url}
                                                alt=""
                                                style={{
                                                    width: 70,
                                                    height: 70,
                                                    objectFit: 'cover',
                                                    borderRadius: 8
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    </Fade>
                </Grid>

                {/* INFO */}
                <Grid item xs={12} md={6}>
                    <Fade in timeout={600}>
                        <Box>
                            <Stack direction="row" spacing={1} mb={1}>
                                {!product.ativo && <Chip label="Inativo" size="small" />}
                                {product.category_name && <Chip label={product.category_name} size="small" variant="outlined" />}
                            </Stack>

                            <Typography variant="h3" fontWeight="700" mb={1}>
                                {product.nome}
                            </Typography>

                            <Typography color="text.secondary" mb={1}>
                                {[product.brand_name, product.model_name].filter(Boolean).join(' • ') || 'Sem marca/modelo'}
                            </Typography>

                            <Typography variant="h2" fontWeight="700" mb={1} color="primary">
                                R$ {valorVenda}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" mb={3}>
                                Custo: R$ {valorCusto}
                            </Typography>

                            <Grid container spacing={1} mb={3}>
                                {product.unit_of_measure_code && (
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Unidade
                                        </Typography>
                                        <Typography variant="body2">{product.unit_of_measure_code}</Typography>
                                    </Grid>
                                )}
                                {product.ncm && (
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            NCM
                                        </Typography>
                                        <Typography variant="body2">{product.ncm}</Typography>
                                    </Grid>
                                )}
                                {product.cest && (
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            CEST
                                        </Typography>
                                        <Typography variant="body2">{product.cest}</Typography>
                                    </Grid>
                                )}
                                {selectedVariation?.sku && (
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            SKU
                                        </Typography>
                                        <Typography variant="body2">{selectedVariation.sku}</Typography>
                                    </Grid>
                                )}
                                {selectedVariation?.barcode && (
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Cód. barras
                                        </Typography>
                                        <Typography variant="body2">{selectedVariation.barcode}</Typography>
                                    </Grid>
                                )}
                                {selectedVariation?.weight && (
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Peso
                                        </Typography>
                                        <Typography variant="body2">{selectedVariation.weight} kg</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Estoque
                                    </Typography>
                                    <Typography variant="body2">{selectedVariation?.estoque ?? 0}</Typography>
                                </Grid>
                            </Grid>

                            {product.variacao?.length > 0 && (
                                <Box mb={3}>
                                    <Typography fontWeight="600" mb={1}>
                                        Escolha uma opção
                                    </Typography>

                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {product.variacao.map((v, i) => (
                                            <Chip
                                                key={i}
                                                label={v.nome_variacao}
                                                color={selectedVariation?.id === v.id ? 'primary' : 'default'}
                                                onClick={() => setSelectedVariation(v)}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                </Grid>

                {/* ACTIONS */}
                <Stack direction="row" mt={3} spacing={2}>
                    <Button variant="contained" onClick={() => history.push(`/dashboard/config/product/edit/${product.id}`)}>
                        Editar
                    </Button>

                    <Button variant="outlined" onClick={() => history.push(`/dashboard/config/products`)}>
                        Voltar
                    </Button>

                    <Button variant="outlined" color="error" onClick={() => deleteProduct(product.id)}>
                        Deletar
                    </Button>
                </Stack>
            </Grid>
        </Box>
    );
};

export default ViewProduct;
